import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import type { ApiError } from '@/core/api';
import { MemberSelect } from '@/features/members';
import { resolveProjectLabel, searchProjects } from '../select';
import type { Allocation, AllocationInput } from '../types';

const schema = z
  .object({
    project: z.string().min(1, 'Project is required'),
    user: z.string().min(1, 'Person is required'),
    role: z.string().max(100),
    hours_per_week: z
      .string()
      .refine((v) => !Number.isNaN(Number(v)) && Number(v) >= 0, 'Enter hours per week'),
    start_date: z.string(),
    end_date: z.string(),
  })
  .refine((v) => !v.end_date || !v.start_date || v.end_date >= v.start_date, {
    message: 'End must be on or after the start.',
    path: ['end_date'],
  });

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = ['project', 'user', 'role', 'hours_per_week', 'start_date', 'end_date'] as const;

interface AllocationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allocation?: Allocation;
  onSubmit: (input: AllocationInput) => Promise<unknown>;
}

function toValues(allocation?: Allocation): FormValues {
  return {
    project: allocation?.project ? String(allocation.project) : '',
    user: allocation?.user ? String(allocation.user) : '',
    role: allocation?.role ?? '',
    hours_per_week: allocation?.hours_per_week ?? '0',
    start_date: allocation?.start_date ?? '',
    end_date: allocation?.end_date ?? '',
  };
}

export function AllocationFormDialog({ open, onOpenChange, allocation, onSubmit }: AllocationFormDialogProps) {
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toValues(allocation) });

  const { reset } = form;
  useEffect(() => {
    if (open) reset(toValues(allocation));
  }, [open, allocation, reset]);

  async function handleSubmit(values: FormValues) {
    const input: AllocationInput = {
      project: Number(values.project),
      user: Number(values.user),
      role: values.role.trim(),
      hours_per_week: values.hours_per_week,
      start_date: values.start_date || null,
      end_date: values.end_date || null,
    };
    try {
      await onSubmit(input);
      onOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{allocation ? 'Edit allocation' : 'New allocation'}</DialogTitle>
          <DialogDescription>Assign a person to a project at a weekly capacity.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormSection title="Person & project">
              <FormField
                control={form.control}
                name="user"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Person</FormLabel>
                    <FormControl>
                      <MemberSelect
                        queryKey="allocation-user-picker"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select a member"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="project"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project</FormLabel>
                    <FormControl>
                      <EntitySelect
                        queryKey="allocation-project-picker"
                        value={field.value}
                        onChange={field.onChange}
                        search={searchProjects}
                        resolveLabel={resolveProjectLabel}
                        placeholder="Select a project"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="Period">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            <FormSection title="Allocation">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="hours_per_week"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hours / week</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" step="0.5" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <FormControl>
                        <Input placeholder="Engineer, Designer…" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : allocation ? 'Save changes' : 'Create allocation'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
